import Collapse from "@common/Collapse";
import { useEffect, useState } from "react";
import infoIcon from "@assets/icons/info.svg";
import uploadActiveIcon from "@assets/icons/upload_active.svg";
import downloadActiveIcon from "@assets/icons/download_active.svg";
import downloadDisabledIcon from "@assets/icons/download_disabled.svg";
import downloadIcon from "@assets/icons/download.svg";
import { SecondaryButton } from "@common/Buttons";
import { Empty, Spin, Tooltip } from "antd";
import { useFetchSectionProductsQuery } from "@store/master";
import {
  useFetchFinalFileMutation,
  useFetchRawAndFinalFileUrlsQuery,
  useFetchRawFileMutation,
  useLazyRegenerateQuery,
} from "@store/file";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { downloadBlobAsFile, downloadUrlAsFile } from "@store/utilities";
import { cleanFileName, toTitleCase } from "@utils/helpers";

const IMPORT_EXPORT_ASYNC_FILE_STATUS = {
  Errored: "Errored",
  Generated: "Generated",
  Generating: "Generating",
};

const CollapseContent = ({
  isLoading,
  data,
  handleRegenerate,
  regenerateLoading = false,
}) => {
  // raw file download
  const handleRawFileDownload = (file) => {
    downloadUrlAsFile(file.url, file.originalName);
  };

  //final file download
  const handleFinalFileDownload = async () => {
    if (data.fetchProductsWiseData) {
      const response = await data.downloadFinalFile(data.activeCollapse, false);
      if (response) {
        downloadBlobAsFile(response.data, cleanFileName(data.raw.originalName));
      }
    } else {
      downloadUrlAsFile(
        data.final.downloadLink,
        cleanFileName(data.final.originalName)
      );
    }
  };

  return isLoading ? (
    <Spin
      className="!mx-auto w-full"
      indicator={<LoadingOutlined spin className="!text-primary-tint" />}
    />
  ) : data.raw ? (
    <div className="flex flex-col gap-3.5">
      {/* <div className="flex justify-between items-center">
        <div className="flex gap-2 items-center">
          <label className="px-2 text-xs !tracking-wider font-semibold rounded-full bg-primary-tint-placeholder text-primary-tint">
            Apr15, 2025
          </label>
          <Tooltip title="If the final file has been modified offline, click to override the existing version with the updated file">
            <img alt="info" src={infoIcon} />
          </Tooltip>
        </div>
        <img
          alt="upload"
          src={uploadActiveIcon}
          className="transition-all duration-300 cursor-pointer hover:scale-105"
        />
      </div> */}
      <div className="flex flex-col gap-1.5">
        <div className="flex gap-2 justify-between items-center">
          <label className="px-2 py-0.5 w-fit text-[10px] !tracking-wider font-semibold rounded-md bg-primary-tint-placeholder text-primary-tint">
            {data.raw.originalName || data.final.originalName}
          </label>
          {regenerateLoading ? (
            <Spin
              indicator={
                <LoadingOutlined spin className="!text-primary-tint" />
              }
              size="small"
            />
          ) : handleRegenerate && typeof handleRegenerate === "function" ? (
            <Tooltip title="Regenerate">
              <ReloadOutlined
                onClick={handleRegenerate}
                className="!cursor-pointer !text-primary-tint"
              />
            </Tooltip>
          ) : null}
        </div>

        <div key={data.raw.url} className="flex items-center gap-2.5">
          <SecondaryButton
            icon={downloadActiveIcon}
            activeIcon={downloadIcon}
            className={"w-full"}
            onClick={() => handleRawFileDownload(data.raw)}
          >
            Raw
          </SecondaryButton>
          <SecondaryButton
            icon={
              data.status === IMPORT_EXPORT_ASYNC_FILE_STATUS.Errored ||
              data.status === IMPORT_EXPORT_ASYNC_FILE_STATUS.Generating ||
              (!data.fetchProductsWiseData && !data.final)
                ? downloadDisabledIcon
                : downloadActiveIcon
            }
            activeIcon={downloadIcon}
            className={"w-full"}
            onClick={handleFinalFileDownload}
            loading={
              data.finalFileLoading ||
              (data.status === IMPORT_EXPORT_ASYNC_FILE_STATUS.Generating &&
                data.final) ||
              regenerateLoading
            }
            disabled={
              data.status === IMPORT_EXPORT_ASYNC_FILE_STATUS.Errored ||
              data.status === IMPORT_EXPORT_ASYNC_FILE_STATUS.Generating ||
              (!data.fetchProductsWiseData && !data.final)
            }
          >
            Final
          </SecondaryButton>
        </div>
      </div>
    </div>
  ) : (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="No files available"
    />
  );
};

export default function TabContent({
  fetchProductsWiseData,
  filteredForMonth,
  filteredForYear,
  fetchRawAndFinalFileUrls,
  activeTab,
  products,
  months,
  typeVal,
  subTypeId,
}) {
  const [activeCollapse, setActiveCollapse] = useState([]);
  const [
    regenerate,
    { isLoading: regenerateLoading, isFetching: regenerateFetching },
  ] = useLazyRegenerateQuery();

  // fetch raw and final file urls
  const {
    data: rawAndFinalFileUrls,
    isLoading: rawAndFinalFileUrlsLoading,
    refetch: refetchRawAndFinalFileUrls,
    isSuccess: rawAndFinalFileUrlsSuccess,
  } = useFetchRawAndFinalFileUrlsQuery(
    {
      url: fetchRawAndFinalFileUrls[activeTab]?.raw,
      params: {
        year: filteredForYear,
        SubTypeMasterId: subTypeId,
      },
    },
    {
      skip: !activeTab || fetchProductsWiseData,
      refetchOnMountOrArgChange: true,
      pollingInterval: 10000,
    }
  );

  // fetch raw file
  const [fetchRawFile, { data: rawFile, rawFileLoading }] =
    useFetchRawFileMutation();

  // fetch final file
  const [fetchFinalFile, { isLoading: finalFileLoading }] =
    useFetchFinalFileMutation();

  const handleFetchRawAndFinalFiles = async (
    activeMonthOrProduct,
    isRaw = true
  ) => {
    const payload = {
      data: {
        year: filteredForYear,
        typeVal,
      },
    };
    if (fetchProductsWiseData) {
      payload.data.month = filteredForMonth;
      payload.data.productName = String(activeMonthOrProduct).toLowerCase();
    } else {
      payload.data.month = activeMonthOrProduct;
    }
    if (isRaw) {
      await fetchRawFile({
        url: fetchRawAndFinalFileUrls[activeTab]?.raw,
        ...payload,
      });
    } else {
      return await fetchFinalFile({
        url: fetchRawAndFinalFileUrls[activeTab]?.final,
        ...payload,
      });
    }
  };

  // regenerate
  const handleRegenerate = () => {
    regenerate({
      SubTypeMasterId: subTypeId,
      month: String(activeCollapse[0]).toLowerCase(),
      year: filteredForYear,
    }).then((res) => {
      if (res?.isSuccess) {
        refetchRawAndFinalFileUrls();
      }
    });
  };

  useEffect(() => {
    if (activeTab && activeCollapse && activeCollapse.length) {
      if (fetchProductsWiseData) {
        handleFetchRawAndFinalFiles(String(activeCollapse[0]).toLowerCase());
      }
    }
  }, [activeTab, filteredForMonth, filteredForYear, activeCollapse]);

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-3 lg:grid-cols-4">
      {fetchProductsWiseData && products && products?.list?.length ? (
        products?.list.map((product) => {
          return (
            <Collapse
              key={product}
              collapseKey={product}
              activeKey={activeCollapse}
              setActiveKey={setActiveCollapse}
              collapsePlaceholderLabel={product}
              collapseContent={
                <CollapseContent
                  data={{
                    raw: rawFile?.data[0],
                    downloadFinalFile: handleFetchRawAndFinalFiles,
                    activeCollapse: activeCollapse?.[0],
                    finalFileLoading: finalFileLoading,
                    fetchProductsWiseData: true,
                  }}
                  isLoading={rawFileLoading}
                />
              }
            />
          );
        })
      ) : fetchProductsWiseData && (!products || !products?.list?.length) ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No products available"
          className="top-1/2 left-1/2 sm:-translate-x-1/2 sm:fixed sm:-translate-y-1/2"
        />
      ) : !fetchProductsWiseData && rawAndFinalFileUrlsLoading ? (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <Spin
            spinning
            indicator={<LoadingOutlined spin className="!text-primary-tint" />}
          />
        </div>
      ) : rawAndFinalFileUrlsSuccess &&
        !rawAndFinalFileUrlsLoading &&
        !fetchProductsWiseData &&
        rawAndFinalFileUrls?.data?.length ? (
        rawAndFinalFileUrls?.data?.map((item) => {
          return (
            <Collapse
              key={item.month}
              collapseKey={item.month}
              activeKey={activeCollapse}
              setActiveKey={setActiveCollapse}
              collapsePlaceholderLabel={toTitleCase(item.month)}
              collapseContent={
                <CollapseContent
                  data={{
                    raw: {
                      originalName: item.RawFiles[0]?.originalName,
                      url: item.RawFiles[0]?.downloadLink,
                    },
                    status: item.status,
                    fetchProductsWiseData: false,
                    final: item.CleanFile,
                    activeCollapse: activeCollapse?.[0],
                  }}
                  isLoading={false}
                  handleRegenerate={handleRegenerate}
                  regenerateLoading={regenerateLoading || regenerateFetching}
                />
              }
            />
          );
        })
      ) : !fetchProductsWiseData ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No months available"
          className="top-1/2 left-1/2 sm:-translate-x-1/2 sm:fixed sm:-translate-y-1/2"
        />
      ) : null}
    </div>
  );
}
